/* global window, document, performance, requestAnimationFrame, cancelAnimationFrame, setTimeout, clearTimeout, Intl */
const logger = {
  info(msg, ...args) {
    // console.log(`[PromotionTabs] ${msg}`, ...args);
  },
};

function loadEmblaCarousel() {
  if (typeof window.importDynamic !== "function") {
    return Promise.reject(new Error("importDynamic not ready"));
  }
  return window.importDynamic("EmblaCarousel");
}

// ============================================
// <promotion-media-carousel>
// ============================================
class PromotionMediaCarousel extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;

    logger.info("PromotionMediaCarousel connectedCallback", {
      id: this.id,
      videoMode: this.dataset.videoMode,
    });

    this._initDom();
    this._bindStaticEvents();
  }

  _initDom() {
    this.track = this.querySelector(".promo-media__track");
    this.slides = Array.from(this.querySelectorAll(".promo-media__slide"));
    this.dotsWrap = this.querySelector("[data-promo-dots]");
    this.dots = this.dotsWrap
      ? Array.from(this.dotsWrap.querySelectorAll("[data-go-to]"))
      : [];
    this.bars = this.dotsWrap
      ? Array.from(this.dotsWrap.querySelectorAll("[data-promo-bar]"))
      : [];
    this.replayBtn = this.querySelector("[data-promo-replay]");

    this.autoplayDelay = parseInt(this.dataset.autoplayDelay, 10) || 5000;
    this.videoMode = this.dataset.videoMode || "replay";
    this._autoplayEnabled = this.dataset.promoAutoplay !== "false";
    this.currentIndex = 0;
    this.embla = null;
    this.timer = null;
    this.rafId = null;
    this.isPaused = false;
    this.isComplete = false;
    this.isDragging = false;
    this.isHovered = false;
    this._imageElapsed = 0;
    this._initialized = false;
    this._initStarted = false;
    this._emblaReady = false;
    this._retryCount = 0;
    this._maxRetries = 40;
    this._panelHidden = false;

    this.videos = [];
    this.slides.forEach((slide) => {
      const v = slide.querySelector("video[data-promo-video]");
      if (v) {
        this.videos.push({ el: v, slide });
        if (this.videoMode === "loop") {
          v.setAttribute("loop", "");
        }
      }
    });
  }

  _bindStaticEvents() {
    this.dots.forEach((dot, i) => {
      dot.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.goTo(i);
      });
    });

    this.addEventListener("mouseenter", () => {
      this.isHovered = true;
      this.pause();
    });
    this.addEventListener("mouseleave", () => {
      this.isHovered = false;
      if (!this.isDragging && !this.isComplete) {
        this.resume();
      }
    });

    this.addEventListener(
      "touchstart",
      () => {
        this.isDragging = true;
        this.pause();
      },
      { passive: true },
    );
    this.addEventListener(
      "touchend",
      () => {
        setTimeout(() => {
          this.isDragging = false;
          if (!this.isHovered && !this.isComplete) {
            this.resume();
          }
        }, 500);
      },
      { passive: true },
    );

    if (this.replayBtn) {
      this.replayBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.replay();
      });
    }

    this.videos.forEach(({ el }) => {
      el.addEventListener("ended", () => this.onVideoEnded(el));
    });

    this._onVisibilityChange = () => {
      if (document.hidden) {
        this.pause();
      } else if (!this.isHovered && !this.isDragging && !this.isComplete) {
        this.resume();
      }
    };
    document.addEventListener("visibilitychange", this._onVisibilityChange);
  }

  /** Check if the parent promotion-panel has is-active class */
  _isPanelVisible() {
    const panel = this.closest(".promotion-panel");
    if (!panel) {
      logger.info(
        "PromotionMediaCarousel _isPanelVisible: no promotion-panel ancestor found",
        { id: this.id },
      );
      return false;
    }
    const visible = panel.classList.contains("is-active");
    if (!visible) {
      logger.info(
        "PromotionMediaCarousel _isPanelVisible: panel is not active",
        {
          id: this.id,
          panelId: panel.dataset.blockId,
          panelClass: panel.className,
        },
      );
    }
    return visible;
  }

  /** Check if this element has any ancestor with display: none */
  _hasVisibleAncestor() {
    return this._isPanelVisible();
  }

  /** Public: activate this carousel (called when panel becomes visible) */
  activate() {
    const panelVisible = this._isPanelVisible();

    logger.info("PromotionMediaCarousel activate START", {
      id: this.id,
      initialized: this._initialized,
      initStarted: this._initStarted,
      emblaReady: this._emblaReady,
      panelHidden: this._panelHidden,
      panelVisible,
      offsetWidth: this.offsetWidth,
      emblaExists: !!this.embla,
    });

    if (!panelVisible) {
      logger.info(
        "PromotionMediaCarousel activate → panel is hidden, deferring init",
        { id: this.id },
      );
      this._panelHidden = true;
      return;
    }

    if (this._panelHidden) {
      logger.info(
        "PromotionMediaCarousel activate → panel now visible, clearing hidden flag",
        { id: this.id },
      );
      this._panelHidden = false;
    }

    this.isPaused = false;

    if (!this._initStarted) {
      logger.info("PromotionMediaCarousel activate → first init path", {
        id: this.id,
        offsetWidth: this.offsetWidth,
      });
      this._initStarted = true;
      this._retryCount = 0;
      this._initEmbla();
      this._initialized = true;
    } else if (!this._emblaReady) {
      if (this._panelHidden) {
        logger.info(
          "PromotionMediaCarousel activate → init was deferred (panel now visible), retrying",
          { id: this.id },
        );
        this._retryCount = 0;
        this._initEmbla();
      } else {
        logger.info(
          "PromotionMediaCarousel activate → init already in progress, waiting",
          { id: this.id },
        );
      }
    } else {
      logger.info("PromotionMediaCarousel activate → reInit path", {
        id: this.id,
        currentIndex: this.currentIndex,
      });
      this._reInit();
    }

    logger.info("PromotionMediaCarousel activate END", {
      id: this.id,
      initStarted: this._initStarted,
      emblaReady: this._emblaReady,
      emblaExists: !!this.embla,
    });
  }

  /** Public: deactivate this carousel (called when panel becomes hidden) */
  deactivate() {
    logger.info("PromotionMediaCarousel deactivate", { id: this.id });
    this.pause();
    this.resetVideos();
  }

  _initEmbla() {
    logger.info("PromotionMediaCarousel _initEmbla START", {
      id: this.id,
      offsetWidth: this.offsetWidth,
      offsetHeight: this.offsetHeight,
      slideCount: this.slides.length,
      isConnected: this.isConnected,
      retryCount: this._retryCount,
    });

    loadEmblaCarousel()
      .then((EmblaCarousel) => {
        if (typeof EmblaCarousel !== "function") {
          this._retryCount++;
          if (this._retryCount > this._maxRetries) {
            logger.info(
              "PromotionMediaCarousel _initEmbla ABORT: max retries exceeded waiting for EmblaCarousel",
              {
                id: this.id,
                retryCount: this._retryCount,
              },
            );
            this._initStarted = false;
            return;
          }
          logger.info(
            "PromotionMediaCarousel _initEmbla DEFERRED: EmblaCarousel not ready",
            {
              id: this.id,
              emblaType: typeof EmblaCarousel,
              retryCount: this._retryCount,
            },
          );
          setTimeout(() => this._initEmbla(), 100);
          return;
        }

        requestAnimationFrame(() => {
          if (!this.isConnected) {
            logger.info(
              "PromotionMediaCarousel _initEmbla ABORT: element disconnected before init",
              { id: this.id },
            );
            this._initStarted = false;
            return;
          }

          const panelVisible = this._isPanelVisible();

          if (!panelVisible) {
            logger.info(
              "PromotionMediaCarousel _initEmbla DEFER: panel is hidden, waiting for activate() call",
              { id: this.id },
            );
            this._panelHidden = true;
            return;
          }

          this._panelHidden = false;

          if (this.offsetWidth === 0) {
            this._retryCount++;
            if (this._retryCount > this._maxRetries) {
              logger.info(
                "PromotionMediaCarousel _initEmbla ABORT: offsetWidth still 0 after max retries",
                {
                  id: this.id,
                  retryCount: this._retryCount,
                },
              );
              this._initStarted = false;
              return;
            }
            if (this._retryCount % 10 === 0) {
              logger.info(
                "PromotionMediaCarousel _initEmbla retry progress (offsetWidth=0 but panel visible)",
                {
                  id: this.id,
                  retryCount: this._retryCount,
                  offsetWidth: this.offsetWidth,
                },
              );
            }
            setTimeout(() => this._initEmbla(), 50);
            return;
          }

          logger.info(
            "PromotionMediaCarousel _initEmbla CREATING Embla instance",
            {
              id: this.id,
              offsetWidth: this.offsetWidth,
              offsetHeight: this.offsetHeight,
              slideCount: this.slides.length,
              retryCount: this._retryCount,
            },
          );

          const emblaStart = performance.now();

          this.embla = EmblaCarousel(this, {
            container: ".promo-media__track",
            slides: ".promo-media__slide",
            align: "start",
            loop: false,
            dragFree: false,
            containScroll: false,
            inViewThreshold: 0.5,
            watchDrag: true,
          });

          const emblaCreated = performance.now();

          this._emblaReady = true;
          this._retryCount = 0;

          logger.info(
            "PromotionMediaCarousel _initEmbla Embla instance created",
            {
              id: this.id,
              createTimeMs: Math.round(emblaCreated - emblaStart),
              snapCount: this.embla.scrollSnapList().length,
              selectedIndex: this.embla.selectedScrollSnap(),
              canScrollPrev: this.embla.canScrollPrev(),
              canScrollNext: this.embla.canScrollNext(),
            },
          );

          this.embla.on("select", () => {
            const idx = this.embla.selectedScrollSnap();
            if (idx !== this.currentIndex) {
              this.onSlideChange(idx);
            }
          });

          this.embla.on("pointerDown", () => {
            this.isDragging = true;
            this.pause();
          });

          this.embla.on("pointerUp", () => {
            this.isDragging = false;
            setTimeout(() => {
              if (!this.isHovered && !this.isDragging && !this.isComplete) {
                this.resume();
              }
            }, 500);
          });

          this.isPaused = false;
          logger.info(
            "PromotionMediaCarousel _initEmbla activating initial slide",
            { id: this.id },
          );
          this.activateSlide(0, false);

          logger.info("PromotionMediaCarousel _initEmbla COMPLETE", {
            id: this.id,
            totalInitTimeMs: Math.round(performance.now() - emblaStart),
            currentIndex: this.currentIndex,
          });
        });
      })
      .catch((err) => {
        logger.info(
          "PromotionMediaCarousel _initEmbla ERROR loading EmblaCarousel",
          {
            id: this.id,
            error: err.message || err,
          },
        );
        this._retryCount++;
        if (this._retryCount <= this._maxRetries) {
          setTimeout(() => this._initEmbla(), 100);
        } else {
          this._initStarted = false;
        }
      });
  }

  _reInit() {
    logger.info("PromotionMediaCarousel _reInit START", {
      id: this.id,
      emblaExists: !!this.embla,
      emblaReady: this._emblaReady,
      initStarted: this._initStarted,
      currentIndex: this.currentIndex,
      offsetWidth: this.offsetWidth,
      slideCount: this.slides.length,
      isConnected: this.isConnected,
    });

    if (!this.embla) {
      if (!this._initStarted) {
        logger.info(
          "PromotionMediaCarousel _reInit FALLBACK: embla is null and init not started, calling _initEmbla",
          {
            id: this.id,
          },
        );
        this._initStarted = true;
        this._retryCount = 0;
        this._initEmbla();
      } else {
        logger.info(
          "PromotionMediaCarousel _reInit SKIP: embla not ready yet but init already in progress, waiting",
          { id: this.id },
        );
      }
      return;
    }

    if (!this._emblaReady) {
      logger.info(
        "PromotionMediaCarousel _reInit: embla exists but _emblaReady is false, marking ready",
        {
          id: this.id,
        },
      );
      this._emblaReady = true;
    }

    logger.info(
      "PromotionMediaCarousel _reInit requesting rAF for embla.reInit()",
      {
        id: this.id,
        currentSnap: this.embla.selectedScrollSnap(),
        snapCount: this.embla.scrollSnapList().length,
        canScrollPrev: this.embla.canScrollPrev(),
        canScrollNext: this.embla.canScrollNext(),
      },
    );

    requestAnimationFrame(() => {
      logger.info("PromotionMediaCarousel _reInit rAF callback START", {
        id: this.id,
        isConnected: this.isConnected,
        offsetWidth: this.offsetWidth,
      });

      if (!this.isConnected) {
        logger.info(
          "PromotionMediaCarousel _reInit ABORT: element disconnected before reInit",
          {
            id: this.id,
          },
        );
        return;
      }

      const reInitStart = performance.now();
      this.embla.reInit();
      const reInitDone = performance.now();

      logger.info("PromotionMediaCarousel _reInit reInit() completed", {
        id: this.id,
        reInitTimeMs: Math.round(reInitDone - reInitStart),
        snapCount: this.embla.scrollSnapList().length,
        selectedIndex: this.embla.selectedScrollSnap(),
        canScrollPrev: this.embla.canScrollPrev(),
        canScrollNext: this.embla.canScrollNext(),
      });

      this.isPaused = false;
      this.currentIndex = 0;
      this.isComplete = false;
      this._imageElapsed = 0;
      this.cancelProgress();

      logger.info("PromotionMediaCarousel _reInit activating slide 0", {
        id: this.id,
      });
      this.activateSlide(0, false);

      logger.info("PromotionMediaCarousel _reInit COMPLETE", {
        id: this.id,
        currentIndex: this.currentIndex,
        totalReInitTimeMs: Math.round(performance.now() - reInitStart),
      });
    });
  }

  get bar() {
    const activeDot = this.dots[this.currentIndex];
    if (!activeDot) return null;
    return activeDot.querySelector("[data-promo-bar]");
  }

  onSlideChange(newIndex) {
    logger.info("PromotionMediaCarousel onSlideChange", {
      newIndex,
      currentIndex: this.currentIndex,
      id: this.id,
    });
    this.clearTimer();
    this.cancelProgress();
    this.resetVideos();
    this.currentIndex = newIndex;
    this._imageElapsed = 0;
    this.updateDots();
    this.activateSlide(newIndex, true);
  }

  activateSlide(idx, fromUser) {
    logger.info("PromotionMediaCarousel activateSlide", {
      idx,
      fromUser,
      id: this.id,
    });
    this.currentIndex = idx;
    this.updateDots();
    this.hideReplay();
    if (this.bar) {
      this.bar.style.width = "0%";
      this.bar.classList.remove("is-video");
    }

    const slide = this.slides[idx];
    if (!slide) return;

    const video = slide.querySelector("video[data-promo-video]");
    if (video) {
      video.currentTime = 0;
      if (!this.isPaused && !this.isDragging) {
        const playPromise = video.play();
        if (playPromise && playPromise.catch) playPromise.catch(() => {});
      }
      this.startVideoProgress(video);
    } else {
      if (this.isComplete) {
        if (this.bar) this.bar.style.width = "100%";
      } else {
        this.startImageTimer();
      }
    }
  }

  resetVideos() {
    this.videos.forEach(({ el }) => {
      el.pause();
      try {
        el.currentTime = 0;
      } catch (e) {}
    });
  }

  startImageTimer() {
    if (this.isPaused || this.isComplete || this.isDragging) return;
    if (!this._autoplayEnabled) return;
    if (!this.bar) return;
    this.bar.classList.remove("is-video");

    const duration = this.autoplayDelay;
    let startTime = performance.now() - this._imageElapsed;

    const tick = (now) => {
      if (this.isPaused || this.isComplete || this.isDragging) {
        this._imageElapsed = now - startTime;
        return;
      }
      const elapsed = now - startTime;
      const pct = Math.min((elapsed / duration) * 100, 100);
      this.bar.style.width = pct + "%";
      if (elapsed >= duration) {
        this._imageElapsed = 0;
        this.advance();
        return;
      }
      this.rafId = requestAnimationFrame(tick);
    };

    if (this._imageElapsed === 0) {
      this.bar.style.width = "0%";
    }
    this.rafId = requestAnimationFrame(tick);
  }

  startVideoProgress(video) {
    if (this.bar) this.bar.classList.add("is-video");
    if (!this.bar && !this.dots.length) return;
    const tick = () => {
      if (this.isPaused || this.isDragging) {
        this.rafId = requestAnimationFrame(tick);
        return;
      }
      if (video.duration) {
        const pct = (video.currentTime / video.duration) * 100;
        if (this.bar) this.bar.style.width = pct + "%";
      }
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  onVideoEnded(videoEl) {
    logger.info("PromotionMediaCarousel onVideoEnded", {
      videoMode: this.videoMode,
      isLast: this.isLastSlide(),
      id: this.id,
    });
    if (this.videoMode === "loop") return;

    if (this.videoMode === "auto_advance") {
      this.cancelProgress();
      if (this.bar) this.bar.style.width = "100%";
      if (this.isLastSlide()) {
        this.embla.scrollTo(0);
      } else {
        this.advance();
      }
      return;
    }

    this.cancelProgress();
    if (this.bar) this.bar.style.width = "100%";

    this.isComplete = true;
    videoEl.pause();
    try {
      videoEl.currentTime = Math.max(0, (videoEl.duration || 0) - 0.02);
    } catch (e) {}
    setTimeout(() => {
      this.showReplay();
    }, 300);
  }

  isLastSlide() {
    return this.currentIndex >= this.slides.length - 1;
  }

  advance() {
    logger.info("PromotionMediaCarousel advance", {
      isLast: this.isLastSlide(),
      videoMode: this.videoMode,
    });
    if (!this.embla) return;
    if (this.isLastSlide()) {
      if (this.videoMode === "loop" || this.videoMode === "auto_advance") {
        this.embla.scrollTo(0);
      } else {
        this.isComplete = true;
        this.showReplay();
      }
    } else {
      this.embla.scrollNext();
    }
  }

  goTo(idx) {
    if (!this.embla) return;
    if (idx === this.currentIndex) return;
    this.isComplete = false;
    this._imageElapsed = 0;
    this.hideReplay();
    this.embla.scrollTo(idx);
  }

  replay() {
    this.isComplete = false;
    this._imageElapsed = 0;
    this.hideReplay();
    if (this.bar) this.bar.style.width = "0%";
    if (this.embla) {
      this.embla.scrollTo(0);
    } else {
      this.onSlideChange(0);
    }
    setTimeout(() => this.resume(), 100);
  }

  pause() {
    if (this.isPaused) return;
    this.isPaused = true;
    this.cancelProgress();
    this.videos.forEach(({ el, slide }) => {
      const idx = this.slides.indexOf(slide);
      if (idx === this.currentIndex) {
        el.pause();
      }
    });
  }

  resume() {
    if (this.isDragging || this.isComplete) return;
    this.isPaused = false;

    const slide = this.slides[this.currentIndex];
    if (!slide) return;
    const video = slide.querySelector("video[data-promo-video]");
    if (video) {
      const playPromise = video.play();
      if (playPromise && playPromise.catch) playPromise.catch(() => {});
      this.startVideoProgress(video);
    } else {
      this.startImageTimer();
    }
  }

  updateDots() {
    this.dots.forEach((dot, i) => {
      dot.classList.toggle("is-active", i === this.currentIndex);
    });
  }

  showReplay() {
    if (this.replayBtn) this.replayBtn.classList.add("is-visible");
    this.slides[this.currentIndex]?.classList.add("is-video-ended");
  }

  hideReplay() {
    if (this.replayBtn) this.replayBtn.classList.remove("is-visible");
    this.slides.forEach((s) => s.classList.remove("is-video-ended"));
  }

  clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  cancelProgress() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  destroy() {
    logger.info("PromotionMediaCarousel destroy", { id: this.id });
    this.clearTimer();
    this.cancelProgress();
    if (this._onVisibilityChange) {
      document.removeEventListener(
        "visibilitychange",
        this._onVisibilityChange,
      );
    }
    if (this.embla) {
      this.embla.destroy();
      this.embla = null;
    }
    this.videos.forEach(({ el }) => {
      el.pause();
      el.removeAttribute("src");
      el.load();
    });
  }

  disconnectedCallback() {
    logger.info("PromotionMediaCarousel disconnectedCallback", { id: this.id });
    this.destroy();
  }
}

// ============================================
// <promotion-carousel>
// ============================================
class PromotionCarousel extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;

    logger.info("PromotionCarousel connectedCallback", { id: this.id });

    this.viewport = this.querySelector(".promotion-carousel-viewport");
    this.prevBtn = this.querySelector(".promotion-carousel-prev");
    this.nextBtn = this.querySelector(".promotion-carousel-next");
    this.embla = null;
    this._initialized = false;
    this._initStarted = false;
    this._emblaReady = false;
    this._retryCount = 0;
    this._maxRetries = 40;
    this._panelHidden = false;

    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", () => {
        logger.info("PromotionCarousel prev clicked");
        if (this.embla) this.embla.scrollPrev();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", () => {
        logger.info("PromotionCarousel next clicked");
        if (this.embla) this.embla.scrollNext();
      });
    }
  }

  /** Check if the parent promotion-panel has is-active class */
  _isPanelVisible() {
    const panel = this.closest(".promotion-panel");
    if (!panel) {
      logger.info(
        "PromotionCarousel _isPanelVisible: no promotion-panel found",
        { id: this.id },
      );
      return false;
    }
    const visible = panel.classList.contains("is-active");
    if (!visible) {
      logger.info("PromotionCarousel _isPanelVisible: panel not active", {
        id: this.id,
        panelId: panel.dataset.blockId,
      });
    }
    return visible;
  }

  _hasVisibleAncestor() {
    return this._isPanelVisible();
  }

  /** Public: activate this carousel when its panel becomes visible */
  activate() {
    const panelVisible = this._isPanelVisible();

    logger.info("PromotionCarousel activate START", {
      id: this.id,
      initialized: this._initialized,
      initStarted: this._initStarted,
      emblaReady: this._emblaReady,
      panelHidden: this._panelHidden,
      panelVisible,
      viewportOffsetWidth: this.viewport ? this.viewport.offsetWidth : -1,
      emblaExists: !!this.embla,
    });

    if (!panelVisible) {
      logger.info(
        "PromotionCarousel activate → panel is hidden, deferring init",
        { id: this.id },
      );
      this._panelHidden = true;
      return;
    }

    if (this._panelHidden) {
      logger.info(
        "PromotionCarousel activate → panel now visible, clearing hidden flag",
        { id: this.id },
      );
      this._panelHidden = false;
    }

    if (!this._initStarted) {
      logger.info("PromotionCarousel activate → first init path", {
        id: this.id,
        viewportOffsetWidth: this.viewport ? this.viewport.offsetWidth : -1,
      });
      this._initStarted = true;
      this._retryCount = 0;
      this._initEmbla();
      this._initialized = true;
    } else if (!this._emblaReady) {
      if (this._panelHidden) {
        logger.info(
          "PromotionCarousel activate → init was deferred (panel now visible), retrying",
          { id: this.id },
        );
        this._retryCount = 0;
        this._initEmbla();
      } else {
        logger.info(
          "PromotionCarousel activate → init already in progress, waiting",
          { id: this.id },
        );
      }
    } else {
      logger.info("PromotionCarousel activate → reInit path", { id: this.id });
      this._reInit();
    }

    logger.info("PromotionCarousel activate END", {
      id: this.id,
      initStarted: this._initStarted,
      emblaReady: this._emblaReady,
      emblaExists: !!this.embla,
    });
  }

  /** Public: deactivate this carousel when its panel becomes hidden */
  deactivate() {
    logger.info("PromotionCarousel deactivate", { id: this.id });
    if (this.embla) {
      this.embla.scrollTo(0);
    }
  }

  _initEmbla() {
    logger.info("PromotionCarousel _initEmbla START", {
      id: this.id,
      viewportExists: !!this.viewport,
      viewportOffsetWidth: this.viewport ? this.viewport.offsetWidth : -1,
      isConnected: this.isConnected,
      retryCount: this._retryCount,
    });

    loadEmblaCarousel()
      .then((EmblaCarousel) => {
        if (typeof EmblaCarousel !== "function") {
          this._retryCount++;
          if (this._retryCount > this._maxRetries) {
            logger.info(
              "PromotionCarousel _initEmbla ABORT: max retries exceeded",
              {
                id: this.id,
                retryCount: this._retryCount,
              },
            );
            this._initStarted = false;
            return;
          }
          setTimeout(() => this._initEmbla(), 100);
          return;
        }

        requestAnimationFrame(() => {
          if (!this.isConnected) {
            logger.info("PromotionCarousel _initEmbla ABORT: disconnected", {
              id: this.id,
            });
            this._initStarted = false;
            return;
          }

          const panelVisible = this._isPanelVisible();

          if (!panelVisible) {
            logger.info(
              "PromotionCarousel _initEmbla DEFER: panel is hidden, waiting for activate()",
              { id: this.id },
            );
            this._panelHidden = true;
            return;
          }

          this._panelHidden = false;

          if (!this.viewport || this.viewport.offsetWidth === 0) {
            this._retryCount++;
            if (this._retryCount > this._maxRetries) {
              logger.info(
                "PromotionCarousel _initEmbla ABORT: offsetWidth still 0 after max retries",
                {
                  id: this.id,
                  retryCount: this._retryCount,
                  viewportOffsetWidth: this.viewport
                    ? this.viewport.offsetWidth
                    : -1,
                },
              );
              this._initStarted = false;
              return;
            }

            if (this._retryCount % 10 === 0) {
              logger.info(
                "PromotionCarousel _initEmbla retry progress (offsetWidth=0 but panel visible)",
                {
                  id: this.id,
                  retryCount: this._retryCount,
                  viewportOffsetWidth: this.viewport
                    ? this.viewport.offsetWidth
                    : -1,
                },
              );
            }
            setTimeout(() => this._initEmbla(), 50);
            return;
          }

          logger.info("PromotionCarousel _initEmbla CREATING Embla instance", {
            id: this.id,
            viewportOffsetWidth: this.viewport.offsetWidth,
            retryCount: this._retryCount,
          });

          const emblaStart = performance.now();

          this.embla = EmblaCarousel(this.viewport, {
            align: "start",
            loop: false,
            containScroll: "trimSnaps",
            inViewThreshold: 0.5,
            slidesToScroll: 1,
            dragFree: false,
          });

          const emblaCreated = performance.now();

          this._emblaReady = true;
          this._retryCount = 0;

          logger.info("PromotionCarousel Embla instance created", {
            id: this.id,
            createTimeMs: Math.round(emblaCreated - emblaStart),
            snapCount: this.embla.scrollSnapList().length,
            selectedIndex: this.embla.selectedScrollSnap(),
            canScrollPrev: this.embla.canScrollPrev(),
            canScrollNext: this.embla.canScrollNext(),
          });

          this.embla.on("select", () => this._updateNav());
          this.embla.on("reInit", () => this._updateNav());
          this._updateNav();

          logger.info("PromotionCarousel _initEmbla COMPLETE", {
            id: this.id,
            totalInitTimeMs: Math.round(performance.now() - emblaStart),
          });
        });
      })
      .catch((err) => {
        logger.info(
          "PromotionCarousel _initEmbla ERROR loading EmblaCarousel",
          {
            id: this.id,
            error: err.message || err,
          },
        );
        this._retryCount++;
        if (this._retryCount <= this._maxRetries) {
          setTimeout(() => this._initEmbla(), 100);
        } else {
          this._initStarted = false;
        }
      });
  }

  _reInit() {
    logger.info("PromotionCarousel _reInit START", {
      id: this.id,
      emblaExists: !!this.embla,
      emblaReady: this._emblaReady,
      initStarted: this._initStarted,
      viewportOffsetWidth: this.viewport ? this.viewport.offsetWidth : -1,
      isConnected: this.isConnected,
    });

    if (!this.embla) {
      if (!this._initStarted) {
        logger.info(
          "PromotionCarousel _reInit FALLBACK: embla null and init not started",
          { id: this.id },
        );
        this._initStarted = true;
        this._retryCount = 0;
        this._initEmbla();
      } else {
        logger.info(
          "PromotionCarousel _reInit SKIP: embla not ready, init in progress",
          { id: this.id },
        );
      }
      return;
    }

    if (!this._emblaReady) {
      this._emblaReady = true;
    }

    logger.info("PromotionCarousel _reInit requesting rAF", {
      id: this.id,
      currentSnap: this.embla.selectedScrollSnap(),
      snapCount: this.embla.scrollSnapList().length,
    });

    requestAnimationFrame(() => {
      logger.info("PromotionCarousel _reInit rAF callback START", {
        id: this.id,
        isConnected: this.isConnected,
        viewportOffsetWidth: this.viewport ? this.viewport.offsetWidth : -1,
      });

      if (!this.isConnected) {
        logger.info("PromotionCarousel _reInit ABORT: disconnected", {
          id: this.id,
        });
        return;
      }

      const reInitStart = performance.now();
      this.embla.reInit();
      const reInitDone = performance.now();

      logger.info("PromotionCarousel _reInit reInit() completed", {
        id: this.id,
        reInitTimeMs: Math.round(reInitDone - reInitStart),
        snapCount: this.embla.scrollSnapList().length,
        selectedIndex: this.embla.selectedScrollSnap(),
        canScrollPrev: this.embla.canScrollPrev(),
        canScrollNext: this.embla.canScrollNext(),
      });

      this.embla.scrollTo(0);
      this._updateNav();

      logger.info("PromotionCarousel _reInit COMPLETE", {
        id: this.id,
        totalReInitTimeMs: Math.round(performance.now() - reInitStart),
      });
    });
  }

  _updateNav() {
    if (!this.embla) return;
    if (this.prevBtn) this.prevBtn.disabled = !this.embla.canScrollPrev();
    if (this.nextBtn) this.nextBtn.disabled = !this.embla.canScrollNext();
  }

  destroy() {
    logger.info("PromotionCarousel destroy", { id: this.id });
    if (this.embla) {
      this.embla.destroy();
      this.embla = null;
    }
  }

  disconnectedCallback() {
    logger.info("PromotionCarousel disconnectedCallback", { id: this.id });
    this.destroy();
  }
}

// ============================================
// <promotion-collection-tabs>
// ============================================
class PromotionCollectionTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll(".promotion-tab"));
    this.panels = Array.from(this.querySelectorAll(".promotion-panel"));
    this.countdownInterval = null;

    logger.info("PromotionCollectionTabs connectedCallback", {
      tabsCount: this.tabs.length,
      panelsCount: this.panels.length,
      tabs: this.tabs.map((t) => t.dataset.blockId),
      panels: this.panels.map((p) => ({ id: p.dataset.blockId })),
    });

    this._currentPanel = null;

    this._bindEvents();
    this.initCountdown();

    const initialPanel = this.querySelector(".promotion-panel.is-active");
    if (initialPanel) {
      requestAnimationFrame(() => {
        this._activatePanelElements(initialPanel);
      });
    }

    if (window.Shopify?.designMode) {
      this._onShopifyBlockSelect = this._onShopifyBlockSelect.bind(this);
      document.addEventListener(
        "shopify:block:select",
        this._onShopifyBlockSelect,
      );
    }
  }

  disconnectedCallback() {
    logger.info("PromotionCollectionTabs disconnectedCallback", {
      countdownActive: !!this.countdownInterval,
    });
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    this.querySelectorAll("promotion-carousel").forEach((el) => {
      if (el.destroy) el.destroy();
    });
    this.querySelectorAll("promotion-media-carousel").forEach((el) => {
      if (el.destroy) el.destroy();
    });

    if (this._onShopifyBlockSelect) {
      document.removeEventListener(
        "shopify:block:select",
        this._onShopifyBlockSelect,
      );
    }
  }

  _bindEvents() {
    this.addEventListener("click", (e) => {
      const tab = e.target.closest(".promotion-tab");
      if (tab) {
        e.preventDefault();
        this.activatePanel(tab.dataset.blockId);
        return;
      }

      const dropdownTrigger = e.target.closest("[data-dropdown-trigger]");
      if (dropdownTrigger) {
        e.preventDefault();
        this.toggleDropdown(dropdownTrigger);
        return;
      }

      const productOption = e.target.closest("[data-product-option]");
      if (productOption) {
        e.preventDefault();
        this.selectBundleProduct(productOption);
        return;
      }

      const ctaBtn = e.target.closest("[data-bundle-product-cta]");
      if (ctaBtn) {
        e.preventDefault();
        this.addToCart(ctaBtn);
        return;
      }

      const dropdown = e.target.closest("[data-product-dropdown]");
      if (!dropdown) {
        this.closeAllDropdowns();
      }
    });

    this.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.closeAllDropdowns();
      }
    });
  }

  toggleDropdown(trigger) {
    const dropdown = trigger.closest("[data-product-dropdown]");
    if (!dropdown) return;

    const panel = dropdown.querySelector("[data-dropdown-panel]");
    const isOpen = trigger.classList.contains("is-open");

    this.closeAllDropdowns();

    if (!isOpen) {
      trigger.classList.add("is-open");
      panel.classList.add("is-open");
      trigger.setAttribute("aria-expanded", "true");
    }
  }

  closeAllDropdowns() {
    this.querySelectorAll("[data-product-dropdown]").forEach((dropdown) => {
      const trigger = dropdown.querySelector("[data-dropdown-trigger]");
      const panel = dropdown.querySelector("[data-dropdown-panel]");
      if (trigger) {
        trigger.classList.remove("is-open");
        trigger.setAttribute("aria-expanded", "false");
      }
      if (panel) panel.classList.remove("is-open");
    });
  }

  addToCart(btn) {
    if (btn.disabled) return;

    const variantId = btn.dataset.productId;
    if (!variantId) return;

    btn.disabled = true;
    btn.classList.add("loading");
    btn.setAttribute("aria-disabled", "true");

    const config = {
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        Accept: "application/javascript",
      },
    };

    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", 1);
    formData.append("sections", "main-cart,cart-drawer,cart-bubble");
    formData.append("sections_url", window.location.pathname);

    config.body = formData;

    const getSectionsToRender = () => [
      { id: "Cart", section: "main-cart", selector: ".thb-cart-form" },
      { id: "Cart-Drawer", section: "cart-drawer", selector: ".cart-drawer" },
      {
        id: "cart-drawer-toggle",
        section: "cart-bubble",
        selector: ".thb-item-count",
      },
    ];

    const getSectionInnerHTML = (html, selector = ".shopify-section") =>
      new DOMParser().parseFromString(html, "text/html").querySelector(selector)
        ?.innerHTML;

    const renderContents = (parsedState) => {
      getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (!el) return;
        const elToReplace = el.querySelector(section.selector) || el;
        const html = getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector,
        );
        if (html) elToReplace.innerHTML = html;
      });

      if (typeof CartDrawer !== "undefined") {
        new CartDrawer();
      }
      if (typeof Cart !== "undefined") {
        new Cart().renderContents(parsedState);
      }

      if (document.getElementById("Cart-Drawer")) {
        document.body.classList.add("open-cc");
        document.getElementById("Cart-Drawer").classList.add("active");
        document.dispatchEvent(new CustomEvent("cart-drawer:open"));
      }
    };

    fetch(`${theme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) return;

        renderContents(response);

        const product = response.hasOwnProperty("items")
          ? response.items[0]
          : response;
        document.dispatchEvent(
          new CustomEvent("cart:item-added", { detail: { product } }),
        );
      })
      .catch((err) => {
        console.error("PromotionCollectionTabs addToCart error", err);
      })
      .finally(() => {
        btn.classList.remove("loading");
        btn.removeAttribute("aria-disabled");
        btn.disabled = false;
      });
  }

  selectBundleProduct(option) {
    const dropdown = option.closest("[data-product-dropdown]");
    if (!dropdown) return;

    const card = dropdown.closest("[data-bundle-card]");
    if (!card) return;

    const productId = option.dataset.productId;
    const productHandle = option.dataset.productHandle;

    logger.info("PromotionCollectionTabs selectBundleProduct", {
      productId,
      productHandle,
    });

    const productsJson = card.querySelector("[data-bundle-products]");
    if (!productsJson) return;

    let products;
    try {
      products = JSON.parse(productsJson.textContent);
    } catch (e) {
      return;
    }

    const product = products.find((p) => String(p.id) === String(productId));
    if (!product) return;

    this.updateBundleCard(card, product);

    const trigger = dropdown.querySelector("[data-dropdown-trigger]");
    const label = dropdown.querySelector("[data-dropdown-label]");
    if (label) label.textContent = product.title;

    dropdown.querySelectorAll("[data-product-option]").forEach((opt) => {
      const isSelected = opt.dataset.productId === productId;
      opt.classList.toggle("is-selected", isSelected);
      opt.setAttribute("aria-selected", isSelected ? "true" : "false");
    });

    this.closeAllDropdowns();
  }

  updateBundleCard(card, product) {
    card.dataset.handle = product.handle;

    const isPurchasable =
      product.available !== false && product.variants_size === 1;
    card.dataset.purchable = isPurchasable ? "true" : "false";

    card.querySelectorAll(".product-featured-image-link").forEach((link) => {
      if (product.url) link.href = product.url;
      if (product.title) link.title = product.title;
    });

    const money = (val) => {
      if (window.theme?.Currency?.formatMoney) {
        const format = window.theme.settings?.moneyFormat || "${{amount}}";
        return window.theme.Currency.formatMoney(val, format);
      }
      try {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 2,
        }).format(val / 100);
      } catch (e) {
        return "$" + (val / 100).toFixed(2);
      }
    };

    const thumbContainer = card.querySelector("[data-bundle-product-thumb]");
    const thumbImg = card.querySelector(".promotion-bundle-hero__product-img");
    const thumbLink = card.querySelector("[data-bundle-product-thumb-link]");
    const placeholder = thumbContainer?.querySelector(".thb-placeholder");

    if (product.thumbnail_url) {
      if (placeholder) placeholder.remove();
      if (thumbImg) {
        thumbImg.src = product.thumbnail_url;
        thumbImg.srcset = "";
        thumbImg.sizes = "(max-width: 767px) 100vw, 300px";
        thumbImg.style.display = "block";
        thumbImg.loading = "lazy";
      }
      if (thumbLink) thumbLink.href = product.url || "#";
    } else {
      if (thumbImg) thumbImg.style.display = "none";
      if (!placeholder && thumbContainer) {
        thumbContainer.innerHTML =
          '<div class="thb-placeholder">' +
          '<svg width="80" height="80" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">' +
          '<rect width="200" height="200" fill="#f5f5f5"/><circle cx="100" cy="75" r="40" fill="#e0e0e0"/>' +
          '<path d="M20 180 L80 120 L140 160 L180 130 L180 180 Z" fill="#e0e0e0"/>' +
          "</svg></div>";
      }
    }
    if (product.thumbnail_alt && thumbImg) {
      thumbImg.alt = product.thumbnail_alt;
    }

    card
      .querySelectorAll("[data-bundle-product-title]")
      .forEach(function (titleEl) {
        titleEl.textContent = product.title;
        if (product.url) titleEl.href = product.url;
      });

    card
      .querySelectorAll("[data-bundle-product-slogan]")
      .forEach(function (sloganEl) {
        if (product.description) {
          sloganEl.textContent = product.description;
        }
      });

    const priceContainer = card.querySelector("[data-price-container]");
    if (priceContainer) {
      const priceSpan = card.querySelector("[data-bundle-product-price]");
      if (priceSpan) {
        const priceVaries = product.price_varies === true;
        const compareAtPriceVaries = product.compare_at_price_varies === true;
        const showCompareAtPrice = !(
          priceVaries === false && compareAtPriceVaries === true
        );
        const isDiscounted =
          showCompareAtPrice &&
          product.compare_at_price != null &&
          product.compare_at_price > 0 &&
          product.price !== product.compare_at_price;

        const priceFormatted = product.price_formatted || money(product.price);
        let priceContent = `<ins><span class="amount ${isDiscounted ? "discounted" : ""}">${priceFormatted}</span></ins>`;

        if (showCompareAtPrice && isDiscounted) {
          const compareAtFormatted =
            product.compare_at_price_formatted ||
            money(product.compare_at_price);
          priceContent += `<del><span class="amount">${compareAtFormatted}</span></del>`;
        }

        priceSpan.innerHTML = priceContent;
      }

      const savedBadge = card.querySelector("[data-saved-badge]");
      if (savedBadge && product.saved_amount > 0) {
        const savedTemplate = savedBadge.dataset.savedText || "Save __AMOUNT__";
        const savedFormatted =
          product.saved_amount_formatted || money(product.saved_amount);
        savedBadge.textContent = savedTemplate.replace(
          "__AMOUNT__",
          savedFormatted,
        );
        savedBadge.style.display = "";
      } else if (savedBadge) {
        savedBadge.style.display = "none";
      }
    }

    card
      .querySelectorAll("[data-bundle-product-badges]")
      .forEach(function (badgesContainer) {
        if (badgesContainer && product.badge_html) {
          badgesContainer.innerHTML = product.badge_html;
        }
      });

    const ctaBtn = card.querySelector("[data-bundle-product-cta]");
    const qvBtn = card.querySelector("[data-bundle-product-qv]");
    const isSingleVariant = product.variants_size === 1;
    const isAvailable = product.available !== false;

    if (ctaBtn && isSingleVariant) {
      const textEl = ctaBtn.querySelector(".product-card--add-to-cart-text");
      ctaBtn.dataset.productId = product.variant_id;
      ctaBtn.disabled = !isAvailable;
      if (textEl) {
        textEl.textContent = isAvailable
          ? window.theme?.variantStrings?.addToCart || "Add to Cart"
          : window.theme?.variantStrings?.soldOut || "Sold out";
      }
      ctaBtn.style.display = "";
      if (qvBtn) qvBtn.style.display = "none";
    } else if (qvBtn && !isSingleVariant) {
      qvBtn.dataset.productHandle = product.handle;
      qvBtn.style.display = "";
      if (ctaBtn) ctaBtn.style.display = "none";
    }

    const qvSecondary = card.querySelector(
      "[data-bundle-product-qv-secondary]",
    );
    if (qvSecondary) {
      qvSecondary.dataset.productHandle = product.handle;
    }
  }

  _activatePanelElements(panel) {
    const carousels = panel.querySelectorAll("promotion-carousel");
    logger.info("PromotionCollectionTabs _activatePanelElements", {
      panelId: panel.dataset.blockId,
      carouselCount: carousels.length,
    });
    carousels.forEach((c) => {
      if (c.activate) c.activate();
    });

    const mediaCarousels = panel.querySelectorAll("promotion-media-carousel");
    logger.info("PromotionCollectionTabs _activatePanelElements media", {
      panelId: panel.dataset.blockId,
      mediaCount: mediaCarousels.length,
    });
    mediaCarousels.forEach((m) => {
      if (m.activate) m.activate();
    });
  }

  activatePanel(blockId) {
    logger.info("PromotionCollectionTabs activatePanel", { blockId });

    let oldActivePanel = null;
    let newActivePanel = null;

    let activeTabEl = null;

    this.tabs.forEach((tab, i) => {
      const isActive = tab.dataset.blockId === blockId;
      tab.classList.toggle("is-active", isActive);
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
      if (isActive) activeTabEl = tab;
    });

    if (activeTabEl) {
      const scrollContainer =
        activeTabEl.closest(".promotion-tabs") || activeTabEl.parentElement;
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const tabRect = activeTabEl.getBoundingClientRect();
        const offset =
          tabRect.left -
          containerRect.left -
          (containerRect.width - tabRect.width) / 2;
        scrollContainer.scrollBy({ left: offset, behavior: "smooth" });
      }
    }

    this.panels.forEach((panel) => {
      const wasActive = panel.classList.contains("is-active");
      const isActive = panel.dataset.blockId === blockId;

      if (wasActive && !isActive) {
        oldActivePanel = panel;
      }

      panel.classList.toggle("is-active", isActive);

      if (isActive) {
        newActivePanel = panel;
      }
    });

    if (oldActivePanel) {
      logger.info("PromotionCollectionTabs deactivating old panel", {
        id: oldActivePanel.dataset.blockId,
      });
      const carousels = oldActivePanel.querySelectorAll(
        "promotion-carousel, promotion-media-carousel",
      );
      carousels.forEach((c) => {
        if (c.deactivate) c.deactivate();
      });
    }

    if (newActivePanel) {
      logger.info("PromotionCollectionTabs activating new panel", {
        id: newActivePanel.dataset.blockId,
      });
      requestAnimationFrame(() => {
        this._activatePanelElements(newActivePanel);
      });
    }

    this._currentPanel = newActivePanel;
  }

  initCountdown() {
    const countdownEl = this.querySelector("[data-countdown-end]");
    if (!countdownEl) return;

    const endStr = countdownEl.dataset.countdownEnd;
    if (!endStr) return;

    const endDate = this._parseCountdownDate(endStr);
    if (!endDate || isNaN(endDate.getTime())) return;

    const daysEl = countdownEl.querySelector('[data-unit="days"]');
    const hoursEl = countdownEl.querySelector('[data-unit="hours"]');
    const minsEl = countdownEl.querySelector('[data-unit="minutes"]');
    const secsEl = countdownEl.querySelector('[data-unit="seconds"]');

    const pad = (n) => String(n).padStart(2, "0");

    const tick = () => {
      const now = new Date().getTime();
      let diff = endDate.getTime() - now;
      if (diff < 0) diff = 0;

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const secs = Math.floor((diff % (1000 * 60)) / 1000);

      if (daysEl) daysEl.textContent = pad(days);
      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minsEl) minsEl.textContent = pad(mins);
      if (secsEl) secsEl.textContent = pad(secs);

      if (diff === 0 && this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    };

    tick();
    this.countdownInterval = setInterval(tick, 1000);
  }

  _parseCountdownDate(str) {
    const s = str.trim();
    const m = s.match(
      /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/,
    );
    if (!m) return null;
    const [, y, mo, d, h = "0", mi = "0", se = "0"] = m;
    return new Date(+y, +mo - 1, +d, +h, +mi, +se);
  }

  _onShopifyBlockSelect(e) {
    const blockId = e.detail.blockId;
    if (!blockId) return;
    const panel = this.querySelector(
      `.promotion-panel[data-block-id="${blockId}"]`,
    );
    if (panel) {
      this.activatePanel(blockId);
    }
  }
}

// Register Custom Elements
if (!customElements.get("promotion-media-carousel")) {
  customElements.define("promotion-media-carousel", PromotionMediaCarousel);
}
if (!customElements.get("promotion-carousel")) {
  customElements.define("promotion-carousel", PromotionCarousel);
}
if (!customElements.get("promotion-collection-tabs")) {
  customElements.define("promotion-collection-tabs", PromotionCollectionTabs);
}
